import React, { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Trophy, Flame, Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/contexts/I18nContext";
import { Section, EmptyState, LoadError } from "@/components/Primitives";
import { StaggerRoot } from "@/components/motion";

export default function ReportBest() {
  const { t, money, lang } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["report-best", from, to],
    queryFn: () => api.get("/reports/best-selling", { params: { date_from: from, date_to: to } }).then((r) => r.data),
    retry: 1,
  });

  if (isLoading) return <div className="p-10 text-gray-500">{t("loading")}</div>;
  if (isError) return <LoadError message={apiError(error)} onRetry={() => refetch()} />;

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("bestSelling")}</h1>
          <p className="text-sm text-gray-500 mt-1">{from} → {to}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm" />
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-medium hover:bg-gray-50">
            <Printer className="w-4 h-4" /> {t("print")}
          </button>
        </div>
      </div>

      <>
          <Section title={t("topItems")}>
            {data.items.length === 0 ? <EmptyState icon={Flame} title={t("empty")} /> : (
              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={data.items.slice(0, 10)} layout="vertical">
                    <CartesianGrid stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} />
                    <YAxis type="category" dataKey={lang === "pashto" ? "name_pashto" : "name_dari"} stroke="#374151" fontSize={12} width={140} />
                    <Tooltip formatter={(v) => v} contentStyle={{ borderRadius: 12, fontSize: 12, direction: "rtl" }} />
                    <Bar dataKey="qty" fill="#071A33" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section title={t("bestSelling")}>
            {data.items.length === 0 ? <EmptyState title={t("empty")} /> : (
              <div className="overflow-x-auto thin-scroll">
                <table className="w-full text-right text-sm">
                  <thead className="text-xs text-gray-500 bg-gray-50">
                    <tr>
                      <th className="p-3">#</th>
                      <th className="p-3">{t("name")}</th>
                      <th className="p-3">{t("quantity")}</th>
                      <th className="p-3">{t("revenue")}</th>
                      <th className="p-3">{t("netProfit")}</th>
                      <th className="p-3">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((it, i) => (
                      <tr key={it.id} className="border-t border-gray-100">
                        <td className="p-3">
                          {i < 3 ? (
                            <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-gray-100 text-gray-700" : "bg-orange-100 text-orange-700"}`}>
                              <Trophy className="w-3.5 h-3.5" />
                            </span>
                          ) : (
                            <span className="tabular text-gray-500">{i + 1}</span>
                          )}
                        </td>
                        <td className="p-3 text-gray-900 font-medium">{lang === "pashto" ? it.name_pashto : it.name_dari}</td>
                        <td className="p-3 tabular font-bold text-[#071A33]">{it.qty}</td>
                        <td className="p-3 tabular">{money(it.revenue)}</td>
                        <td className="p-3 tabular text-emerald-600">{money(it.profit)}</td>
                        <td className="p-3 tabular text-gray-500">{it.percent.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
      </>
    </StaggerRoot>
  );
}
